import { CornerDownLeft, Hash, Search, Volume2 } from '@/components/ui/icons';
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { PublicUser } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { conversationApi } from '@/api/endpoints';
import { Avatar, CountBadge, GroupAvatar, ServerIcon } from '@/components/ui/Avatar';
import { Modal } from '@/components/ui/Modal';
import { conversationTitle, recipientsOf } from '@/lib/conversations';
import { isChannelUnread } from '@/lib/readState';
import { cn } from '@/lib/utils';
import { useServers } from '@/stores/servers';
import { useConversations, useFriends, useReadStates } from '@/stores/social';
import { toast } from '@/stores/ui';
import { usePresence, useUsers } from '@/stores/users';

type Item =
  | { kind: 'channel'; key: string; id: string; serverId: string; name: string; voice: boolean; serverName: string; unread: boolean; mentions: number }
  | { kind: 'dm'; key: string; conversationId: string; user: PublicUser; unread: number }
  | { kind: 'group'; key: string; conversationId: string; name: string; members: PublicUser[]; unread: number }
  | { kind: 'friend'; key: string; user: PublicUser }
  | { kind: 'server'; key: string; id: string; name: string; iconUrl: string | null; unread: boolean; mentions: number };

type Scope = 'all' | 'text' | 'voice' | 'people' | 'servers';

const SCOPES: Record<string, Scope> = { '#': 'text', '!': 'voice', '@': 'people', '*': 'servers' };

/** Higher is better; 0 means no match. */
export function matchScore(name: string, query: string): number {
  if (!query) return 1;
  const n = name.toLowerCase();
  const q = query.toLowerCase();
  if (n === q) return 100;
  if (n.startsWith(q)) return 80;
  if (n.split(/[\s\-_.]+/).some((word) => word.startsWith(q))) return 60;
  if (n.includes(q)) return 40;
  // Loose subsequence match ("gnrl" → "general").
  let i = 0;
  for (const ch of n) if (ch === q[i]) i++;
  return i === q.length ? 15 : 0;
}

function Highlight({ text, query }: { text: string; query: string }) {
  const index = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className="rounded-sm bg-accent/25 text-fg">{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

/** Ctrl+K: jump to any server, channel, DM or friend by typing a few letters. */
export function QuickSwitcher({ onClose }: { onClose: () => void }) {
  const [raw, setRaw] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();

  const servers = useServers((s) => s.servers);
  const order = useServers((s) => s.order);
  const channels = useServers((s) => s.channels);
  const reads = useReadStates((s) => s.channels);
  const conversations = useConversations((s) => s.conversations);
  const friends = useFriends((s) => s.friends);
  const users = useUsers((s) => s.users);
  const presences = usePresence((s) => s.statuses);

  const scope: Scope = SCOPES[raw[0] ?? ''] ?? 'all';
  const query = (scope === 'all' ? raw : raw.slice(1)).trim();

  const allItems = useMemo<Item[]>(() => {
    const items: Item[] = [];
    for (const channel of Object.values(channels)) {
      const server = servers[channel.serverId];
      if (!server) continue;
      const read = reads[channel.id];
      items.push({
        kind: 'channel',
        key: `c:${channel.id}`,
        id: channel.id,
        serverId: channel.serverId,
        name: channel.name,
        voice: channel.type === 'VOICE',
        serverName: server.name,
        unread: !read?.muted && isChannelUnread(channel, read?.lastReadAt, server.me.joinedAt),
        mentions: read?.mentionCount ?? 0,
      });
    }
    const dmUsers = new Set<string>();
    for (const c of Object.values(conversations)) {
      if (c.type === 'GROUP') {
        items.push({ kind: 'group', key: `d:${c.id}`, conversationId: c.id, name: conversationTitle(c, users), members: recipientsOf(c, users), unread: c.unreadCount });
        continue;
      }
      const recipient = c.recipients[0];
      if (!recipient) continue;
      dmUsers.add(recipient.id);
      items.push({ kind: 'dm', key: `d:${c.id}`, conversationId: c.id, user: users[recipient.id] ?? recipient, unread: c.unreadCount });
    }
    for (const f of Object.values(friends)) {
      if (!dmUsers.has(f.user.id)) items.push({ kind: 'friend', key: `f:${f.user.id}`, user: users[f.user.id] ?? f.user });
    }
    for (const id of order) {
      const server = servers[id];
      if (!server) continue;
      const serverChannels = items.filter((i): i is Extract<Item, { kind: 'channel' }> => i.kind === 'channel' && i.serverId === id);
      items.push({
        kind: 'server',
        key: `s:${id}`,
        id,
        name: server.name,
        iconUrl: server.iconUrl,
        unread: server.me.notificationLevel !== 'NOTHING' && serverChannels.some((c) => c.unread),
        mentions: serverChannels.reduce((sum, c) => sum + c.mentions, 0),
      });
    }
    return items;
  }, [channels, servers, order, reads, conversations, friends, users]);

  const results = useMemo(() => {
    const inScope = allItems.filter((item) => {
      if (scope === 'text') return item.kind === 'channel' && !item.voice;
      if (scope === 'voice') return item.kind === 'channel' && item.voice;
      if (scope === 'people') return item.kind === 'dm' || item.kind === 'group' || item.kind === 'friend';
      if (scope === 'servers') return item.kind === 'server';
      return true;
    });

    if (!query) {
      // Nothing typed yet: surface what probably needs attention.
      const attention = (item: Item) =>
        item.kind === 'channel' ? item.mentions * 10 + (item.unread ? 1 : 0) : item.kind === 'dm' || item.kind === 'group' ? item.unread * 10 : 0;
      const ranked = inScope
        .filter((item) => item.kind !== 'server' && item.kind !== 'friend')
        .map((item) => ({ item, score: attention(item) }))
        .sort((a, b) => b.score - a.score);
      const recentDms = Object.values(conversations)
        .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt))
        .map((c) => `d:${c.id}`);
      const picked: Item[] = ranked.filter((r) => r.score > 0).map((r) => r.item);
      for (const key of recentDms) {
        const item = inScope.find((i) => i.key === key);
        if (item && !picked.includes(item)) picked.push(item);
      }
      if (scope !== 'all' && picked.length === 0) return inScope.slice(0, 12);
      return picked.slice(0, 12);
    }

    return inScope
      .map((item) => {
        const names =
          item.kind === 'dm' || item.kind === 'friend'
            ? [item.user.displayName, item.user.username]
            : item.kind === 'group'
              ? [item.name, ...item.members.map((u) => u.displayName)]
              : [item.name];
        let score = Math.max(...names.map((n) => matchScore(n, query)));
        if (score === 0) return null;
        if (item.kind === 'channel') score += (item.mentions ? 5 : 0) + (item.unread ? 2 : 0) - (item.voice ? 1 : 0);
        if (item.kind === 'dm' || item.kind === 'group') score += item.unread ? 5 : 3;
        return { item, score };
      })
      .filter((r): r is { item: Item; score: number } => r !== null)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map((r) => r.item);
  }, [allItems, scope, query, conversations]);

  const select = async (item: Item | undefined) => {
    if (!item) return;
    onClose();
    if (item.kind === 'channel') navigate(`/app/${item.serverId}/${item.id}`);
    else if (item.kind === 'server') navigate(`/app/${item.id}`);
    else if (item.kind === 'dm' || item.kind === 'group') navigate(`/app/@me/${item.conversationId}`);
    else {
      try {
        const conversation = await conversationApi.open(item.user.id);
        useConversations.getState().upsert(conversation);
        navigate(`/app/@me/${conversation.id}`);
      } catch (err) {
        toast.error(errorMessage(err));
      }
    }
  };

  const move = (delta: number) => {
    const next = (active + delta + results.length) % Math.max(results.length, 1);
    setActive(next);
    listRef.current?.querySelector<HTMLElement>(`[data-index="${next}"]`)?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      void select(results[active]);
    }
  };

  const renderItem = (item: Item): { icon: ReactNode; label: ReactNode; context?: string; badge?: ReactNode } => {
    switch (item.kind) {
      case 'channel':
        return {
          icon: item.voice ? <Volume2 size={18} /> : <Hash size={18} />,
          label: <span className={cn(item.unread && 'font-bold text-fg')}><Highlight text={item.name} query={query} /></span>,
          context: item.serverName,
          badge: item.mentions ? <CountBadge count={item.mentions} /> : item.unread ? <span className="h-2 w-2 rounded-full bg-fg" aria-label="Unread" /> : null,
        };
      case 'dm':
      case 'friend':
        return {
          icon: <Avatar user={item.user} size={24} status={presences[item.user.id] ?? 'offline'} ring="ring-elevated" />,
          label: (
            <span>
              <Highlight text={item.user.displayName} query={query} /> <span className="text-fg-muted">@{item.user.username}</span>
            </span>
          ),
          context: item.kind === 'friend' ? 'Friend' : 'Direct message',
          badge: item.kind === 'dm' && item.unread ? <CountBadge count={item.unread} /> : null,
        };
      case 'group':
        return {
          icon: <GroupAvatar users={item.members} size={24} ring="ring-elevated" />,
          label: <span className={cn(item.unread > 0 && 'font-bold text-fg')}><Highlight text={item.name} query={query} /></span>,
          context: 'Group',
          badge: item.unread ? <CountBadge count={item.unread} /> : null,
        };
      case 'server':
        return {
          icon: <ServerIcon server={item} size={24} />,
          label: <span className={cn(item.unread && 'font-bold text-fg')}><Highlight text={item.name} query={query} /></span>,
          context: 'Server',
          badge: item.mentions ? <CountBadge count={item.mentions} /> : null,
        };
    }
  };

  return (
    <Modal open onClose={onClose} bare size="md" className="self-start mt-[12vh]">
      <div className="p-4" onKeyDown={onKeyDown}>
        <p className="mb-3 text-center text-lg font-bold">Where would you like to go?</p>
        <label className="relative block">
          <Search size={18} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-fg-muted" />
          <input
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setActive(0);
            }}
            placeholder="Search servers, channels and people"
            aria-label="Quick switcher"
            aria-controls="quick-switcher-results"
            aria-activedescendant={results[active] ? `qs-${results[active]!.key}` : undefined}
            data-autofocus
            className="h-12 w-full rounded-xl border border-line bg-sunken pr-4 pl-11 text-base text-fg placeholder:text-fg-muted focus:border-accent focus:outline-none"
          />
        </label>
        <p className="mt-3 mb-1.5 px-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">
          {query ? 'Results' : scope === 'all' ? 'Unread & recent' : 'Suggestions'}
        </p>
        <ul ref={listRef} id="quick-switcher-results" role="listbox" className="max-h-[min(360px,50dvh)] overflow-y-auto">
          {results.length === 0 ? (
            <li className="px-3 py-8 text-center text-sm text-fg-muted">{query ? `Nothing matches “${query}”.` : "You're all caught up — start typing to jump anywhere."}</li>
          ) : (
            results.map((item, index) => {
              const view = renderItem(item);
              return (
                <li
                  key={item.key}
                  id={`qs-${item.key}`}
                  data-index={index}
                  role="option"
                  aria-selected={index === active}
                  onMouseMove={() => setActive(index)}
                  onClick={() => void select(item)}
                  className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2', index === active ? 'bg-active text-fg' : 'text-fg-soft')}
                >
                  <span className="flex w-6 shrink-0 justify-center text-fg-muted">{view.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{view.label}</span>
                  {view.badge}
                  {view.context && <span className="max-w-[40%] shrink-0 truncate text-xs text-fg-muted">{view.context}</span>}
                  {index === active && <CornerDownLeft size={14} className="shrink-0 text-fg-muted" aria-hidden />}
                </li>
              );
            })
          )}
        </ul>
        <p className="mt-3 border-t border-line pt-3 text-center text-xs text-fg-muted">
          Tip: start with <kbd className="rounded bg-hover px-1">#</kbd> for text channels, <kbd className="rounded bg-hover px-1">!</kbd> voice,{' '}
          <kbd className="rounded bg-hover px-1">@</kbd> people, <kbd className="rounded bg-hover px-1">*</kbd> servers ·{' '}
          <kbd className="rounded bg-hover px-1">Ctrl</kbd>+<kbd className="rounded bg-hover px-1">/</kbd> all shortcuts
        </p>
      </div>
    </Modal>
  );
}
